import { useCallback, useMemo } from "react";
import ENV_VARIABLES from "@/env";
import { useCallbackOnInterval } from "@/hooks/useCallbackOnInterval";
import { useUserStore } from "@/store/user";
import { BalanceType } from "@/types/user";
import { getPaygMonthlyEstimate, getPaygRunwayHours, getPaygRunwayLevel } from "@/utils/payg";

/** Matches the cluster detail page's billing refresh. */
const REFRESH_INTERVAL_MS = 30000;

type UseAccountBurnRateOptions = {
  /**
   * Poll for updates. Off by default: the header pill and sidebar are mounted app-wide,
   * so polling there would poll on every route — only a page that shows this as its own
   * stat (the Funds page) should opt in.
   */
  poll?: boolean;
};

/**
 * What the account's running PAYG clusters cost per hour, plus what that means for the
 * credits balance: a monthly projection and how long the balance lasts at this rate.
 */
export const useAccountBurnRate = ({ poll = false }: UseAccountBurnRateOptions = {}) => {
  const userId = useUserStore((state) => state.userId);
  const balances = useUserStore((state) => state.balances);
  const burnRate = useUserStore((state) => state.burnRate);
  const fetchBurnRate = useUserStore((state) => state.fetchBurnRate);
  const isEnabled = Boolean(ENV_VARIABLES.FEATUREFLAG_PAYG_ENABLED) && Boolean(userId);

  const refresh = useCallback(async () => {
    if (!isEnabled || !userId) return;
    try {
      await fetchBurnRate(true);
    } catch {
      // Decoration next to the balance — keep whatever was last known.
    }
  }, [fetchBurnRate, isEnabled, userId]);

  // Fires immediately, then on the interval only when the caller asked to poll.
  useCallbackOnInterval(refresh, poll ? REFRESH_INTERVAL_MS : undefined);

  return useMemo(() => {
    const hourlyRate = isEnabled ? (burnRate?.hourlyRate ?? null) : null;
    const creditBalance = Number(balances?.[BalanceType.CREDITS] ?? 0);
    const runwayHours = getPaygRunwayHours(creditBalance, hourlyRate);
    const runwayLevel = getPaygRunwayLevel(runwayHours);

    return {
      isPaygEnabled: isEnabled,
      hourlyRate,
      clusterCount: burnRate?.clusterCount ?? 0,
      monthlyEstimate: hourlyRate === null ? null : getPaygMonthlyEstimate(hourlyRate),
      runwayHours,
      creditBalance,
      runwayLevel,
      isLow: runwayLevel !== "ok",
      isCritical: runwayLevel === "critical",
      isVisible: hourlyRate !== null && hourlyRate > 0,
      refresh
    };
  }, [balances, burnRate, isEnabled, refresh]);
};
